var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Col, Layout, MessageBanner, Modal, RadioButton, RadioButtonGroup, Row, TextArea, } from "@folio/stripes/components";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQueryClient } from "react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RecekSelection, RecekTextField } from "../inputs";
import { usePublishersData } from "../../hooks/usePublishersData";
import { usePeriodicalFrequencyOptions } from "../../hooks/usePeriodicalFrequencyOptions";
import { formatPublisherLabel } from "../../utils/publisherLabel";
import { normalizeIssn } from "../../utils/issn";
import { provisionalKey } from "../../utils/provisionalTitleKeys";
import { createProvisionalTitle, updateProvisionalTitle, } from "../../api/titleService";
import { useResendAgencyNotification } from "../../hooks/useAgencyNotifications";
import { agencyNotificationsQueryKey, } from "../../types/agencyNotification";
import { AgencyNotificationStatusBadge } from "./AgencyNotificationStatusBadge";
import css from "./AgencyNotificationsSection.module.css";
var createSchema = function (formatMessage) {
    return z.object({
        name: z
            .string()
            .trim()
            .min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.validation.required",
            }),
        }),
        author: z.string().trim().optional(),
        isbn: z.string().trim().optional(),
        issn: z
            .string()
            .trim()
            .optional()
            .refine(function (value) { return !value || normalizeIssn(value) !== null; }, {
            message: formatMessage({
                id: "ui-br-legaldeposits.provisionalTitle.field.issnInvalid",
            }),
        }),
        frequency: z.string().optional(),
        year: z
            .string()
            .trim()
            .optional()
            .refine(function (value) {
            if (!value)
                return true;
            var parsed = Number(value);
            return Number.isInteger(parsed) && parsed >= 1500 && parsed <= 2100;
        }, {
            message: formatMessage({
                id: "ui-br-legaldeposits.provisionalTitle.field.yearInvalid",
            }),
        }),
        publisherMode: z.enum(["registry", "manual"]),
        publisherId: z.string().optional(),
        publisherName: z.string().trim().optional(),
        place: z.string().trim().optional(),
        note: z
            .string()
            .trim()
            .min(1, {
            message: formatMessage({
                id: "ui-br-legaldeposits.validation.required",
            }),
        }),
    });
};
var emptyValues = {
    name: "",
    author: "",
    isbn: "",
    issn: "",
    frequency: "",
    year: "",
    publisherMode: "registry",
    publisherId: "",
    publisherName: "",
    place: "",
    note: "",
};
export var ProvisionalTitleModal = function (_a) {
    var _b, _c, _d;
    var open = _a.open, onClose = _a.onClose, onCreated = _a.onCreated, titleId = _a.titleId, _e = _a.titleType, titleType = _e === void 0 ? "MONOGRAPH" : _e, initialValues = _a.initialValues, _f = _a.showNotifyAgency, showNotifyAgency = _f === void 0 ? false : _f, _g = _a.disableIsbn, disableIsbn = _g === void 0 ? false : _g;
    var formatMessage = useIntl().formatMessage;
    var isPeriodical = titleType === "PERIODICAL";
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var callout = useCallout();
    var _h = useState(null), agencyResult = _h[0], setAgencyResult = _h[1];
    var _j = useState(""), publisherQuery = _j[0], setPublisherQuery = _j[1];
    var _k = useState(""), selectedPublisherId = _k[0], setSelectedPublisherId = _k[1];
    var _l = useState(true), notifyAgency = _l[0], setNotifyAgency = _l[1];
    var _m = useForm({
        resolver: zodResolver(createSchema(formatMessage)),
        defaultValues: emptyValues,
        mode: "onSubmit",
        shouldFocusError: false,
    }), handleSubmit = _m.handleSubmit, control = _m.control, reset = _m.reset, setError = _m.setError, setValue = _m.setValue, watch = _m.watch, errors = _m.formState.errors;
    var publisherMode = watch("publisherMode");
    var hasIsbn = !!((_b = watch("isbn")) === null || _b === void 0 ? void 0 : _b.trim());
    var _o = usePublishersData(publisherQuery, selectedPublisherId), publishers = _o.publishers, isPublishersLoading = _o.isPublishersLoading, selectedPublisher = _o.selectedPublisher;
    var publisherOptions = useMemo(function () {
        var entries = publishers.map(function (publisher) { return ({
            value: publisher.id || "",
            label: formatPublisherLabel(publisher),
        }); });
        var extra = (selectedPublisher === null || selectedPublisher === void 0 ? void 0 : selectedPublisher.id) &&
            (selectedPublisher === null || selectedPublisher === void 0 ? void 0 : selectedPublisher.name) &&
            !entries.some(function (e) { return e.value === selectedPublisher.id; })
            ? {
                value: selectedPublisher.id,
                label: formatPublisherLabel(selectedPublisher),
            }
            : null;
        return __spreadArray(__spreadArray([
            {
                value: "",
                label: formatMessage({
                    id: "ui-br-legaldeposits.provisionalTitle.field.publisherSelectEmpty",
                }),
            }
        ], (extra ? [extra] : []), true), entries, true);
    }, [publishers, selectedPublisher, formatMessage]);
    var periodicalFrequencyOptions = usePeriodicalFrequencyOptions();
    var frequencyOptions = useMemo(function () { return __spreadArray([
        {
            value: "",
            label: formatMessage({
                id: "ui-br-legaldeposits.provisionalTitle.field.frequencyEmpty",
            }),
        }
    ], periodicalFrequencyOptions, true); }, [periodicalFrequencyOptions, formatMessage]);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (!open) {
            reset(emptyValues);
            setPublisherQuery("");
            setSelectedPublisherId("");
            setNotifyAgency(true);
            setAgencyResult(null);
            return;
        }
        // Agency registers only ISBN titles — without ISBN there is nothing to notify
        setNotifyAgency(!disableIsbn);
        var mode = (initialValues === null || initialValues === void 0 ? void 0 : initialValues.publisherName) || (initialValues === null || initialValues === void 0 ? void 0 : initialValues.place)
            ? "manual"
            : "registry";
        reset({
            name: (_a = initialValues === null || initialValues === void 0 ? void 0 : initialValues.name) !== null && _a !== void 0 ? _a : "",
            author: (_b = initialValues === null || initialValues === void 0 ? void 0 : initialValues.author) !== null && _b !== void 0 ? _b : "",
            isbn: (_c = initialValues === null || initialValues === void 0 ? void 0 : initialValues.isbn) !== null && _c !== void 0 ? _c : "",
            issn: (_d = initialValues === null || initialValues === void 0 ? void 0 : initialValues.issn) !== null && _d !== void 0 ? _d : "",
            frequency: (_e = initialValues === null || initialValues === void 0 ? void 0 : initialValues.frequency) !== null && _e !== void 0 ? _e : "",
            year: (initialValues === null || initialValues === void 0 ? void 0 : initialValues.year) ? String(initialValues.year) : "",
            publisherMode: mode,
            publisherId: (_f = initialValues === null || initialValues === void 0 ? void 0 : initialValues.publisherId) !== null && _f !== void 0 ? _f : "",
            publisherName: (_g = initialValues === null || initialValues === void 0 ? void 0 : initialValues.publisherName) !== null && _g !== void 0 ? _g : "",
            place: (_h = initialValues === null || initialValues === void 0 ? void 0 : initialValues.place) !== null && _h !== void 0 ? _h : "",
            note: (_j = initialValues === null || initialValues === void 0 ? void 0 : initialValues.note) !== null && _j !== void 0 ? _j : "",
        });
        setSelectedPublisherId((_k = initialValues === null || initialValues === void 0 ? void 0 : initialValues.publisherId) !== null && _k !== void 0 ? _k : "");
        setPublisherQuery((_l = initialValues === null || initialValues === void 0 ? void 0 : initialValues.publisherName) !== null && _l !== void 0 ? _l : "");
    }, [open, initialValues, reset, disableIsbn]);
    var invalidateTitleQueries = useCallback(function () {
        queryClient.invalidateQueries(["titlesForVerification"]);
        queryClient.invalidateQueries(["titlesWithIsbn"]);
        queryClient.invalidateQueries(["titlesWithoutIsbn"]);
        queryClient.invalidateQueries(["title"]);
        queryClient.invalidateQueries(["title-detail"]);
        queryClient.invalidateQueries(["title-enforcement-detail"]);
        queryClient.invalidateQueries(["periodicalLibraries"]);
    }, [queryClient]);
    var showSaveError = useCallback(function () {
        setError("note", {
            type: "server",
            message: formatMessage({
                id: provisionalKey(titleType, "error.save"),
            }),
        });
    }, [setError, formatMessage, titleType]);
    var sendAgencySentCallout = useCallback(function () {
        callout === null || callout === void 0 ? void 0 : callout.sendCallout({
            type: "success",
            message: formatMessage({
                id: "ui-br-legaldeposits.agencyNotification.callout.sent",
            }),
        });
    }, [callout, formatMessage]);
    var createMutation = useMutation(function (payload) {
        return createProvisionalTitle(kyInstance, payload);
    }, {
        onSuccess: function (created, payload) {
            invalidateTitleQueries();
            onCreated === null || onCreated === void 0 ? void 0 : onCreated({
                pairedWithRegistryPublisher: !!payload.publisherId,
                titleId: created.title.id,
            });
            var notification = created.agencyNotification;
            if (notification && created.title.id) {
                queryClient.invalidateQueries(agencyNotificationsQueryKey(created.title.id));
                if (notification.status !== "SENT") {
                    setAgencyResult({ titleId: created.title.id, notification: notification });
                    return;
                }
                sendAgencySentCallout();
            }
            onClose();
        },
        onError: showSaveError,
    });
    var updateMutation = useMutation(function (payload) {
        return updateProvisionalTitle(kyInstance, titleId, payload);
    }, {
        onSuccess: function () {
            invalidateTitleQueries();
            onClose();
        },
        onError: showSaveError,
    });
    var handleResent = useCallback(function (notification) {
        if (notification.status === "SENT") {
            sendAgencySentCallout();
            onClose();
            return;
        }
        setAgencyResult(function (previous) {
            return previous ? __assign(__assign({}, previous), { notification: notification }) : previous;
        });
    }, [sendAgencySentCallout, onClose]);
    var resendMutation = useResendAgencyNotification(handleResent);
    var resetResend = resendMutation.reset;
    useEffect(function () {
        if (!open)
            resetResend();
    }, [open, resetResend]);
    var handleResend = useCallback(function () {
        if (!agencyResult)
            return;
        resendMutation.mutate({
            titleId: agencyResult.titleId,
            notificationId: agencyResult.notification.id,
        });
    }, [agencyResult, resendMutation]);
    var isSaving = createMutation.isLoading || updateMutation.isLoading;
    var handleModeChange = useCallback(function (mode) {
        setValue("publisherMode", mode);
        if (mode === "registry") {
            setValue("publisherName", "");
            setValue("place", "");
        }
        else {
            setValue("publisherId", "");
            setSelectedPublisherId("");
            setPublisherQuery("");
        }
    }, [setValue]);
    var handleClose = useCallback(function () {
        reset(emptyValues);
        setPublisherQuery("");
        setSelectedPublisherId("");
        setNotifyAgency(true);
        setAgencyResult(null);
        onClose();
    }, [reset, onClose]);
    var handleFormSubmit = useCallback(function (data) {
        var _a;
        var author = data.author.trim();
        var isbn = data.isbn.trim();
        var issn = data.issn.trim();
        var year = data.year.trim();
        var publisherName = data.publisherName.trim();
        var place = data.place.trim();
        var useRegistry = data.publisherMode === "registry";
        var payload = {
            name: data.name.trim(),
            author: author || undefined,
            type: titleType,
            isbn: isPeriodical || disableIsbn ? undefined : isbn || undefined,
            issn: isPeriodical ? ((_a = normalizeIssn(issn)) !== null && _a !== void 0 ? _a : undefined) : undefined,
            frequency: isPeriodical ? data.frequency || undefined : undefined,
            year: isPeriodical ? undefined : year ? Number(year) : undefined,
            publisherId: useRegistry ? data.publisherId || null : null,
            publisherName: useRegistry ? null : publisherName || null,
            place: useRegistry ? null : place || null,
            note: data.note.trim(),
            notifyAgency: showNotifyAgency && !isPeriodical && !disableIsbn && isbn
                ? notifyAgency
                : undefined,
        };
        if (titleId) {
            updateMutation.mutate(payload);
        }
        else {
            createMutation.mutate(payload);
        }
    }, [
        titleId,
        createMutation,
        updateMutation,
        showNotifyAgency,
        notifyAgency,
        disableIsbn,
        titleType,
        isPeriodical,
    ]);
    return (React.createElement(Modal, { open: open, label: formatMessage({
            id: titleId
                ? provisionalKey(titleType, "editModalTitle")
                : provisionalKey(titleType, "modalTitle"),
        }), onClose: handleClose, size: "small", footer: agencyResult ? (React.createElement(Layout, { className: "display-flex justify-end" },
            React.createElement(Button, { onClick: handleClose, "data-test-id": "agency-notification-result-close" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.common.close" })),
            React.createElement(Button, { buttonStyle: "primary", disabled: resendMutation.isLoading, onClick: handleResend, "data-test-id": "agency-notification-result-resend" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agencyNotification.resend" })))) : (React.createElement(Layout, { className: "display-flex justify-end" },
            React.createElement(Button, { onClick: handleClose },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.cancel" })),
            React.createElement(Button, { buttonStyle: "primary", disabled: isSaving, onClick: handleSubmit(handleFormSubmit) },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.save" })))) }, agencyResult ? (React.createElement("div", { className: css.resultBody, "data-test-id": "agency-notification-result" },
        React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agencyNotification.result.failed", values: {
                    reason: (_c = agencyResult.notification.errorMessage) !== null && _c !== void 0 ? _c : formatMessage({
                        id: "ui-br-legaldeposits.agencyNotification.reason.notAttempted",
                    }),
                } })),
        React.createElement("div", null,
            React.createElement(AgencyNotificationStatusBadge, { status: agencyResult.notification.status }),
            " ", (_d = agencyResult.notification.recipientAddress) !== null && _d !== void 0 ? _d : ""),
        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agencyNotification.result.nextStep" }),
        resendMutation.isError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agencyNotification.resend.failed" }))))) : (React.createElement(React.Fragment, null,
        React.createElement(Controller, { control: control, name: "name", render: function (_a) {
                var _b;
                var field = _a.field;
                return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-legaldeposits.provisionalTitle.field.name", required: true, error: (_b = errors.name) === null || _b === void 0 ? void 0 : _b.message })));
            } }),
        React.createElement(Controller, { control: control, name: "author", render: function (_a) {
                var _b;
                var field = _a.field;
                return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-legaldeposits.provisionalTitle.field.author", error: (_b = errors.author) === null || _b === void 0 ? void 0 : _b.message })));
            } }),
        isPeriodical ? (React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "issn", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekTextField, __assign({}, field, { labelId: provisionalKey(titleType, "field.issn"), error: (_b = errors.issn) === null || _b === void 0 ? void 0 : _b.message })));
                    } })),
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "frequency", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekSelection, { labelId: "ui-br-legaldeposits.provisionalTitle.field.frequency", name: "frequency", value: field.value, onChange: field.onChange, onBlur: field.onBlur, dataOptions: frequencyOptions, error: (_b = errors.frequency) === null || _b === void 0 ? void 0 : _b.message }));
                    } })))) : (React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "isbn", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-legaldeposits.provisionalTitle.field.isbn", disabled: disableIsbn, error: (_b = errors.isbn) === null || _b === void 0 ? void 0 : _b.message })));
                    } })),
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "year", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekTextField, __assign({}, field, { type: "number", labelId: "ui-br-legaldeposits.provisionalTitle.field.year", error: (_b = errors.year) === null || _b === void 0 ? void 0 : _b.message })));
                    } })))),
        React.createElement(Controller, { control: control, name: "publisherMode", render: function (_a) {
                var field = _a.field;
                return (React.createElement(RadioButtonGroup, { label: formatMessage({
                        id: "ui-br-legaldeposits.provisionalTitle.field.publisher",
                    }) },
                    React.createElement(RadioButton, { label: formatMessage({
                            id: "ui-br-legaldeposits.provisionalTitle.field.publisherMode.registry",
                        }), value: "registry", checked: field.value === "registry", onChange: function () { return handleModeChange("registry"); } }),
                    React.createElement(RadioButton, { label: formatMessage({
                            id: "ui-br-legaldeposits.provisionalTitle.field.publisherMode.manual",
                        }), value: "manual", checked: field.value === "manual", onChange: function () { return handleModeChange("manual"); } })));
            } }),
        publisherMode === "registry" ? (React.createElement(Controller, { control: control, name: "publisherId", render: function (_a) {
                var _b;
                var field = _a.field;
                return (React.createElement(RecekSelection, { labelId: "ui-br-legaldeposits.provisionalTitle.field.publisherSelect", name: "publisherId", value: field.value, asyncFilter: true, loading: isPublishersLoading, onFilter: function (val) { return setPublisherQuery(val); }, onChange: function (id) {
                        field.onChange(id);
                        setSelectedPublisherId(id);
                    }, dataOptions: publisherOptions, error: (_b = errors.publisherId) === null || _b === void 0 ? void 0 : _b.message, marginBottom0: true }));
            } })) : (React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "publisherName", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-legaldeposits.provisionalTitle.field.publisherNameManual", error: (_b = errors.publisherName) === null || _b === void 0 ? void 0 : _b.message })));
                    } })),
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "place", render: function (_a) {
                        var _b;
                        var field = _a.field;
                        return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-legaldeposits.provisionalTitle.field.place", error: (_b = errors.place) === null || _b === void 0 ? void 0 : _b.message })));
                    } })))),
        React.createElement(Controller, { control: control, name: "note", render: function (_a) {
                var _b;
                var field = _a.field;
                return (React.createElement(TextArea, __assign({}, field, { label: formatMessage({
                        id: provisionalKey(titleType, "field.note"),
                    }), placeholder: formatMessage({
                        id: provisionalKey(titleType, "field.noteHelp"),
                    }), required: true, error: (_b = errors.note) === null || _b === void 0 ? void 0 : _b.message })));
            } }),
        showNotifyAgency && !disableIsbn && !isPeriodical && (React.createElement(Checkbox, { checked: notifyAgency && hasIsbn, disabled: !hasIsbn, label: formatMessage({
                id: "ui-br-legaldeposits.provisionalTitle.field.notifyAgency",
            }), onChange: function (e) {
                return setNotifyAgency(e.target.checked);
            } }))))));
};
